import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { FiSend, FiMic, FiMicOff, FiImage, FiPaperclip, FiX, FiPause, FiPlay, FiTrash } from 'react-icons/fi';

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(239,68,68,0.7); }
  70% { box-shadow: 0 0 0 12px rgba(239,68,68,0.0); }
  100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.0); }
`;

const InputContainer = styled.div`
  padding: 16px;
  background: white;
  border-top: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 72px;
  box-sizing: border-box;
  position: relative;
`;

// 👇 ye styled component add karo
const IconWithLabel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 10px;
  margin-top: 2px;
  color: inherit;
IconWithLabel
  span {
    margin-top: 2px;
  }
`;


const InputWrapper = styled.div`
  flex: 1;
  position: relative;
  min-height: 40px;
  display: flex;
  align-items: center;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 40px;
  max-height: 120px;
  padding: 10px 16px;
  border: 1.5px solid #e5e7eb;
  border-radius: 20px;
  resize: none;
  font-family: inherit;
  font-size: 15px;
  line-height: 1.4;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s, height 0.15s;
  box-sizing: border-box;
  margin: 0;
  background: #fff;
  box-shadow: none;
  overflow-y: hidden;
  /* Hide scrollbars for all browsers */
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
  /* Center placeholder text vertically */
  &::placeholder {
    color: #9ca3af;
    line-height: 1.4;
    vertical-align: middle;
    display: block;
    position: relative;
    top: 0;
  }

  &:focus {
    border-color: #E89B3C;
    box-shadow: 0 0 0 3px rgba(232, 155, 60, 0.10);
  }

  &:disabled {
    background: #f3f4f6;
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' | 'recording' }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  position: relative;

  ${props => {
    if (props.variant === 'danger') {
      return `
        background: #ef4444;
        color: white;
        
        &:hover:not(:disabled) {
          background: #dc2626;
        }
      `;
    }
    
    if (props.variant === 'recording') {
      return `
        background: #dc2626;
        color: white;
        animation: pulse 1.5s infinite;
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
      `;
    }
    
    if (props.variant === 'secondary') {
      return `
        background: #f3f4f6;
        color: #6b7280;
        
        &:hover:not(:disabled) {
          background: #e5e7eb;
        }
      `;
    }
    
    return `
      background: #E89B3C;
      color: white;
      
      &:hover:not(:disabled) {
        background: #FFD700;
        color: #E89B3C;
      }
    `;
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const VoiceButton = styled.button<{ $isRecording: boolean; $isPaused: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  position: relative;
  background: ${props => props.$isRecording ? '#dc2626' : '#f3f4f6'};
  color: ${props => props.$isRecording ? 'white' : '#6b7280'};
  
  ${props => props.$isRecording && `
    animation: ${props.$isPaused ? 'none' : 'pulse 1.5s infinite'};
    @keyframes pulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.1); }
    }
  `}

  &:hover:not(:disabled) {
    background: ${props => props.$isRecording ? '#b91c1c' : '#e5e7eb'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RecordingOverlay = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(245, 245, 245, 0.98); /* light grey */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center; /* center vertically */
  z-index: 1500;
  opacity: ${props => props.$isVisible ? 1 : 0};
  visibility: ${props => props.$isVisible ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
`;

const RecordingControlsColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const RecordingControlsRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 60px; /* 🔥 gap increase kiya */
`;


const SideIconButton = styled.button<{ variant?: "cancel" | "send" }>`
  width: 37px;
  height: 37px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  cursor: pointer;
  transition: background 0.2s, transform 0.2s;
  outline: none;
  color: white;

  ${({ variant }) =>
    variant === "cancel"
      ? `
        background: #ef4444; /* red */
        &:hover { background: #dc2626; }
      `
      : `
        background: #00b32dff; /* orange */
        &:hover { background: #00b32dff; color: rgba(60, 232, 103, 1)ff; }
      `}
`;


const AnimatedRecordButton = styled.button<{ $isRecording: boolean }>`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: none;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.12);
  position: relative;
  cursor: pointer;
  outline: none;
  transition: box-shadow 0.15s;
  &:hover, &:focus {
    box-shadow: 0 4px 16px rgba(0,0,0,0.18);
  }
  &::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #ef4444;
    box-shadow: 0 0 0 0 rgba(239,68,68,0.7);
    animation: ${props => props.$isRecording ? css`${pulse} 1.2s infinite` : 'none'};
    z-index: 1;
  }
`;

const RecordIconInner = styled.div`
  position: relative;
  z-index: 2;
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const RecordingTimer = styled.div`
  margin-top: 6px;
  font-size: 18px;
  font-weight: 700;
  color: #333;
  letter-spacing: 1px;
  text-align: center;
`;

const RecordingContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-bottom: 64px;
`;

const RecordingIcon = styled.div<{ $isPaused: boolean }>`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  animation: ${props => props.$isPaused ? 'none' : 'pulse 1s infinite'};
  
  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.1); }
  }
`;

const RecordingTime = styled.div`
  font-size: 24px;
  font-weight: 700;
  font-family: monospace;
`;

const RecordingHint = styled.div`
  font-size: 14px;
  opacity: 0.8;
  max-width: 200px;
  text-align: center;
`;

const RecordingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  font-size: 14px;
  font-weight: 500;
  animation: pulse 2s infinite;

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
`;

const RecordingDot = styled.div`
  width: 8px;
  height: 8px;
  background: #dc2626;
  border-radius: 50%;
  animation: blink 1s infinite;

  @keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0.3; }
  }
`;

const FileInput = styled.input`
  display: none;
`;

const PopoverMenu = styled.div`
  position: absolute;
  bottom: 56px;
  left: 0;
  background: #fff;
  border: 1.5px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.08);
  padding: 8px 0;
  min-width: 120px;
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PopoverItem = styled.button`
  background: none;
  border: none;
  width: 100%;
  padding: 10px 18px;
  text-align: left;
  font-size: 15px;
  color: #232B3A;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.15s;
  &:hover {
    background: #f3f4f6;
  }
`;

const PopoverClipper = styled.div`
  position: relative;
  margin-left: 8px;
  display: flex;
  align-items: center;
`;

const ActionButtons = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 20px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  z-index: 3000;
  padding: 0 20px;
  pointer-events: none;
`;

const ActionButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  pointer-events: auto;
`;

const PausePlayButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: #fff;
  color: #E89B3C;
  box-shadow: 0 1px 4px rgba(0,0,0,0.10);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  cursor: pointer;
  transition: transform 0.12s, box-shadow 0.12s, background 0.12s;
  outline: none;
  &:hover, &:focus {
    transform: scale(1.08);
    box-shadow: 0 2px 8px rgba(0,0,0,0.18);
    background: #FFE6C7;
  }
`;

const SendButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: #E89B3C;
  color: #fff;
  box-shadow: 0 1px 4px rgba(0,0,0,0.10);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  cursor: pointer;
  transition: transform 0.12s, box-shadow 0.12s, background 0.12s;
  outline: none;
  &:hover, &:focus {
    transform: scale(1.08);
    box-shadow: 0 2px 8px rgba(0,0,0,0.18);
    background: #FFD700;
    color: #E89B3C;
  }
`;

const TrashButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: #ef4444;
  color: #fff;
  box-shadow: 0 1px 4px rgba(0,0,0,0.10);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  cursor: pointer;
  transition: transform 0.12s, box-shadow 0.12s, background 0.12s;
  outline: none;
  margin-right: 8px;
  &:hover, &:focus {
    transform: scale(1.08);
    box-shadow: 0 2px 8px rgba(0,0,0,0.18);
    background: #dc2626;
  }
`;

const ImageModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ImageModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
`;

const ImagePreview = styled.div`
  margin-bottom: 16px;
  text-align: center;
  
  img {
    max-width: 100%;
    max-height: 300px;
    border-radius: 8px;
    object-fit: contain;
  }
`;

const ImageDescriptionInput = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 12px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  resize: vertical;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.4;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
  margin-bottom: 16px;

  &:focus {
    border-color: #E89B3C;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const ModalButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 8px 16px;
  border-radius: 6px;
  border: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => props.variant === 'primary' ? `
    background: #E89B3C;
    color: white;
    
    &:hover:not(:disabled) {
      background: #d68a2b;
    }
    
    &:disabled {
      background: #f3f4f6;
      color: #9ca3af;
      cursor: not-allowed;
    }
  ` : `
    background: #f3f4f6;
    color: #374151;
    
    &:hover {
      background: #e5e7eb;
    }
  `}
`;

interface MessageInputProps {
  onSendMessage: (message: string) => void;
  onRecord: () => void;
  onStopRecording: () => void;
  onPauseRecording: () => void;
  onResumeRecording: () => void;
  onCancelRecording: () => void;
  onSendImage?: (imageFile: File, description: string) => void;
  isRecording: boolean;
  isPaused: boolean;
  recordingTime: number;
  loading: boolean;
  disabled?: boolean;
   t: (key: string) => string; // ✅ add this
}

const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  onRecord,
  onStopRecording,
  onPauseRecording,
  onResumeRecording,
  onCancelRecording,
  onSendImage,
  isRecording,
  isPaused,
  recordingTime,
  loading,
  disabled = false,
  t
}) => {
  const [message, setMessage] = useState('');
  const [isComposing, setIsComposing] = useState(false);
  const [showPopover, setShowPopover] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageDescription, setImageDescription] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea as user types
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [message]);

  // Close popover on outside click
  useEffect(() => {
    if (!showPopover) return;
    const handleClick = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('.popover-clipper')) {
        setShowPopover(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showPopover]);

  const handleSend = () => {
    if (message.trim() && !loading && !disabled) {
      onSendMessage(message.trim());
      setMessage('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleCompositionStart = () => setIsComposing(true);
  const handleCompositionEnd = () => setIsComposing(false);

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      console.log('Image selected:', file);
      
      // Check file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        alert('Image file is too large. Please select an image smaller than 5MB.');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file (JPG, PNG, GIF, WebP).');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      
      setSelectedImage(file);
      setShowImageModal(true);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleImageSend = () => {
    if (selectedImage && imageDescription.trim() && onSendImage) {
      onSendImage(selectedImage, imageDescription.trim());
      setSelectedImage(null);
      setImageDescription('');
      setShowImageModal(false);
    }
  };

  const handleImageCancel = () => {
    setSelectedImage(null);
    setImageDescription('');
    setShowImageModal(false);
  };

  const handleMicClick = async () => {
    if (!isRecording) {
      // Start recording
      await onRecord();
    }
  };

  const handleCancelClick = () => {
    if (isRecording) {
      onCancelRecording();
    }
  };

  const handleSendClick = () => {
    if (isRecording) {
      onStopRecording();
    } else {
      handleSend();
    }
  };

  const handleTimerClick = () => {
    if (isRecording) {
      if (isPaused) {
        onResumeRecording();
      } else {
        onPauseRecording();
      }
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleClipClick = () => setShowPopover(v => !v);
  const handleImageClick = () => {
    setShowPopover(false);
    fileInputRef.current?.click();
  };

  const canSend = message.trim() && !loading && !disabled;

  return (
    <>
      {showImageModal && selectedImage && (
        <ImageModal>
          <ImageModalContent>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
              Add Description for Image
            </h3>
            <ImagePreview>
              <img src={URL.createObjectURL(selectedImage)} alt="Preview" />
            </ImagePreview>
            <div style={{ 
              fontSize: '11px', 
              color: '#6b7280', 
              textAlign: 'center', 
              marginBottom: '12px'
            }}>
              📁 {selectedImage.name} ({(selectedImage.size / 1024 / 1024).toFixed(2)}MB)
              <br />
              <span style={{ fontSize: '10px' }}>
                Max size: 5MB • Formats: JPG, PNG, GIF, WebP
              </span>
            </div>
            <ImageDescriptionInput
              value={imageDescription}
              onChange={(e) => setImageDescription(e.target.value)}
              placeholder="Describe what you want to know about this image (required)"
              disabled={loading}
            />
            {loading && (
              <div style={{ 
                fontSize: '12px', 
                color: '#6b7280', 
                textAlign: 'center', 
                marginBottom: '12px',
                fontStyle: 'italic'
              }}>
                ⏱️ Image analysis may take up to 60 seconds...
              </div>
            )}
            <ModalButtons>
              <ModalButton onClick={handleImageCancel} disabled={loading}>
                Cancel
              </ModalButton>
              <ModalButton 
                variant="primary" 
                onClick={handleImageSend}
                disabled={loading || !imageDescription.trim()}
              >
                {loading ? 'Sending...' : 'Send Image'}
              </ModalButton>
            </ModalButtons>
          </ImageModalContent>
        </ImageModal>
      )}
      <InputContainer>
{isRecording && (
  <RecordingOverlay $isVisible={isRecording}>
    <RecordingControlsColumn className="flex flex-col items-center justify-center">
      {/* Buttons Row */}
      <RecordingControlsRow className="flex items-center justify-center gap-10">
        {/* Cancel Button with label */}
        <div className="flex flex-col items-center">
          <SideIconButton variant="cancel" onClick={handleCancelClick}>
            <FiX size={22} />
          </SideIconButton>
          <span className="text-[12px] mt-1 text-red-500 font-medium">
            {t("cancel")}
          </span>
        </div>

        {/* Send Button with label */}
        <div className="flex flex-col items-center ">
          {/* 👆 moved slightly down using translate-y */}
          <SideIconButton variant="send" onClick={handleSendClick}>
            <FiSend size={22} className="translate-y-0.5 mr-0.5 "  />
          </SideIconButton>
          <span className="text-[12px] mt-1 text-green-600 font-medium">
            {t("send")}
          </span>
        </div>
      </RecordingControlsRow>

 
      <RecordingTimer className="mb-8 text-white text-sm font-semibold">
        {formatTime(recordingTime)}
      </RecordingTimer>
    </RecordingControlsColumn>
  </RecordingOverlay>
)}
 <FileInput
    type="file"
    accept="image/*"
    ref={fileInputRef}
    onChange={handleImageUpload}
  />

      <InputWrapper>
        <TextArea
          ref={textareaRef}
          value={message}
          onChange={handleMessageChange}
          onKeyDown={handleKeyDown}
          onCompositionStart={handleCompositionStart}
          onCompositionEnd={handleCompositionEnd}
          placeholder={t('textPlaceholder')}
          disabled={loading || disabled || isRecording}
          rows={1}
        />
<div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
  {/* Upload Button */}
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
<Button
  type="button"
  variant="secondary"
  aria-label="Attach"
  onClick={() => fileInputRef.current?.click()} // ✅ open file picker directly
  disabled={loading || disabled || isRecording}
  style={{
    width: "30px",
    height: "30px",
    marginLeft: "8px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }}
>
  <FiPaperclip size={20} />
</Button>

    <span style={{ fontSize: "12px", color: "#555" }}>{t("upload")}</span>
  </div>

  {/* Mic Button */}
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
    {!isRecording ? (
      <VoiceButton
        $isRecording={false}
        $isPaused={false}
        onClick={handleMicClick}
        disabled={loading || disabled}
        aria-label="Start voice recording"
        style={{
 width: "30px",
        height: "30px",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <FiMic size={20} />
      </VoiceButton>
    ) : (
      <VoiceButton
        $isRecording={true}
        $isPaused={isPaused}
        onClick={handleCancelClick}
        disabled={loading || disabled}
        aria-label="Cancel voice recording"
        style={{
 width: "30px",
        height: "30px",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <FiTrash size={20} />
      </VoiceButton>
    )}
    <span style={{ fontSize: "12px", color: "#555" }}>
      {isRecording ? t("cancel") : t("mic")}
    </span>
  </div>

  {/* Send Button */}
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
    <Button
      type="button"
      aria-label={isRecording ? "Send voice message" : "Send"}
      onClick={handleSendClick}
      disabled={loading || disabled || (!message.trim() && !isRecording)}
      style={{
 width: "30px",
        height: "30px",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#f4c27f",
      }}
    >
      <FiSend size={20} />
    </Button>
    <span style={{ fontSize: "12px", marginTop: "4px", color: "#555" }}>{t("send")}</span>
  </div>
</div>

      </InputWrapper>
    </InputContainer>
    </>
  );
};

export default MessageInput; 